import { html, KatapultElement } from '../../mixins/katapult-element';
import { html as litHtml } from 'lit';
import '@polymer/paper-menu-button/paper-menu-button.js';
import '../paper-header/paper-header.js';
import '../katapult-firebase-worker/katapult-firebase-worker.js';
import '../katapult-elements/katapult-button.js';
import { DialogService } from '../../modules/DialogService';
import { SharingLink } from '../sharing-link/sharing-link.js';

window.customElements.define(
  'cross-server-job-sharing',
  class extends KatapultElement {
    static get template() {
      return html`
        <katapult-firebase-worker
          path="photoheight/company_space/[[companyId]]/sharing_links/job_sharing"
          data="{{jobSharingLinks}}"
          disabled="[[disabled]]"
        ></katapult-firebase-worker>

        <paper-header-content>
          <paper-cell slot="header" style="cursor: pointer; gap: var(--sl-spacing-x-small);">
            <material-icon icon="share"></material-icon>
            <span>Cross Server Job Sharing</span>
          </paper-cell>
          <div slot="content" style="display: flex; flex-direction: column; gap: 12px; padding: 12px;">
            <div style="display: flex; justify-content: center; gap: 8px;">
              <katapult-button size="18" color="var(--secondary-color)" icon="add_link" callback="generateSharingLink"
                >Create Sharing Code</katapult-button
              >
              <katapult-button size="18" color="var(--secondary-color)" icon="handshake" callback="enterSharingLink"
                >Enter Sharing Code</katapult-button
              >
            </div>
            <template is="dom-repeat" items="[[jobSharingLinksArray]]">
              <sharing-link link-id="[[item.$key]]" sharing-link="[[item]]" company-id="[[companyId]]"></sharing-link>
            </template>
          </div>
        </paper-header-content>
      `;
    }

    constructor() {
      super();
    }

    static get properties() {
      return {
        jobSharingLinks: {
          type: Object,
          value: null
        },
        jobSharingLinksArray: {
          type: Array,
          computed: 'calcJobSharingLinksArray(jobSharingLinks, serverTimeOffset)'
        },
        serverTimeOffset: {
          type: Number,
          value: 0
        }
      };
    }

    ready() {
      super.ready();
      FirebaseWorker.ref('.info/serverTimeOffset')
        .once('value')
        .then((s) => (this.serverTimeOffset = s.val()));
    }

    calcJobSharingLinksArray() {
      return Object.entries(this.jobSharingLinks ?? {})
        .map(([key, x]) => {
          // Set the other company's details under the key `otherCompany` for easy access.
          const temp = x.sender?.server_id == window.config.appName ? 'recipient' : 'sender';
          x.otherCompany = x[temp];
          x.$key = key;
          return x;
        })
        .filter((x) => {
          // Hide expired pending links.
          return x.pending == null || x.expires_at == null || x.expires_at > Date.now() + this.serverTimeOffset;
        });
    }

    async generateSharingLink(e) {
      const button = e.currentTarget;
      await SharingLink.serverAction({ action: 'create', linkType: 'job_sharing', companyId: this.companyId });
      button.showSuccess();
    }

    async enterSharingLink() {
      // Create the dialog.
      const { dialog, promise } = DialogService.dialog({
        properties: { title: `Enter Job Sharing Code`, icon: 'handshake' },
        bodyStyle: 'text-align: center;',
        template: litHtml`
        <div slot="body" id="inputView">
          <p>Please enter a job sharing code below:</p>
          <paper-input label="Sharing Code"></paper-input>
        </div>
        <div slot="body" id="confirmView" style="text-align: center; display: none;">
          <p>You are about to establish a job sharing link with <strong class="companyName"></strong> from the <strong class="serverName"></strong> server.</p>
          <p>Once complete, both you and <strong class="companyName"></strong> will be able to share jobs between servers.</p>
          <p>Would you like to continue?</p>
        </div>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <div slot="buttons" style="flex-grow: 1;"></div>
        <katapult-button slot="buttons" id="confirm" justify-icon="right" color="var(--secondary-color)" disabled>Continue</katapult-button>
      `
      });
      // Create a placeholder for storing the entered token.
      let token = '';
      // Get the confirm button.
      const button = dialog.querySelector('katapult-button[id="confirm"]');
      // Get the input and setup a value listener on it to enable the confirm button when valid.
      const input = dialog.querySelector('paper-input');
      input.addEventListener('value-changed', (e) => {
        token = e.detail.value?.trim();
        const valid = SharingLink.tokenFormatValid(token);
        input.invalid = token.length && !valid;
        input.errorMessage = input.invalid ? 'Must be a 10 digit code consisting of numbers and letters' : null;
        button.disabled = !valid;
        button.icon = valid ? 'arrow_forward' : null;
      });
      // Click button when enter pressed.
      input.addEventListener('keypress', (e) => e.key == 'Enter' && !button.disabled && button.click());
      // Setup callback on the button to get sharing link.
      button.callback = async () => {
        const { sharingLink } = await SharingLink.serverAction({ action: 'get', token, companyId: this.companyId }).catch((err) => {
          input.invalid = true;
          input.errorMessage = 'Invalid sharing code';
        });
        if (!sharingLink) {
          input.invalid = true;
          input.errorMessage = 'Invalid sharing code';
        } else if (sharingLink.sender.server_id == window.config.appName) {
          input.invalid = true;
          input.errorMessage = 'Cannot create a sharing link between two companies on the same server';
        } else {
          // Update button.
          button.justifyIcon = 'left';
          button.icon = 'check';
          button.innerText = 'Establish Sharing Link';
          button.callback = async () => {
            await SharingLink.serverAction({ action: 'accept', token, companyId: this.companyId });
            setTimeout(() => dialog.close(), 2000);
            return true;
          };
          // Populate sharing details.
          Array.from(dialog.querySelectorAll('.companyName')).forEach((x) => (x.innerText = sharingLink.sender.company));
          Array.from(dialog.querySelectorAll('.serverName')).forEach((x) => (x.innerText = sharingLink.sender.server));
          // Hide input view and show confirmation view.
          dialog.querySelector('#inputView').style.display = 'none';
          dialog.querySelector('#confirmView').style.display = '';
        }
      };
      // Focus the input.
      setTimeout(() => input.focus());
    }
  }
);
